import { useEffect, useRef, useCallback } from 'react';
import { useNotificationStore, Notification } from '../stores/notificationStore';

let notificationCounter = 0;

interface WsEvent {
  eventType: string;
  priority: string;
  payload: Record<string, unknown>;
  timestamp: string;
  sourceService: string;
  targetUserIds: number[];
  referenceType: string | null;
  referenceId: number | null;
}

interface UseWebSocketOptions {
  url?: string;
  token: string | null;
  enabled?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export function useWebSocket({
  url = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`,
  token,
  enabled = true,
  reconnectInterval = 3000,
  maxReconnectAttempts = 10,
}: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const { addNotification, setConnected } = useNotificationStore();

  const connect = useCallback(() => {
    if (!token || !enabled) return;

    // Token is sent as the first text message after the upgrade (not as
    // a query parameter) — the gateway's WS handler rejects query-
    // embedded tokens to keep them out of access/proxy logs and
    // browser history. See backend/gateway/src/api/websocket.rs.
    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log('[WS] Connected — sending auth token');
      ws.send(token);
      setConnected(true);
      reconnectCount.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data: WsEvent = JSON.parse(event.data);
        if (data.eventType === 'connected') return;

        // Convert WsEvent to Notification (matches backend NotificationResponse shape)
        const notification: Notification = {
          notificationId: ++notificationCounter,
          userId: 0, // WebSocket events don't carry user ID; set by server
          notificationType: data.eventType,
          category: 'in_app',
          priority: data.priority,
          title: data.eventType.replace(/_/g, ' '),
          message: typeof data.payload === 'object' ? JSON.stringify(data.payload) : String(data.payload),
          sourceType: data.referenceType,
          sourceId: data.referenceId,
          actionUrl: null,
          isRead: false,
          readAt: null,
          isDismissed: false,
          expiresAt: null,
          createdDate: data.timestamp,
        };
        addNotification(notification);
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      console.log('[WS] Disconnected');
      setConnected(false);
      wsRef.current = null;

      // Auto-reconnect with exponential backoff
      if (enabled && reconnectCount.current < maxReconnectAttempts) {
        const delay = reconnectInterval * Math.pow(2, reconnectCount.current);
        reconnectCount.current += 1;
        console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectCount.current})`);
        reconnectTimer.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = (error) => {
      console.error('[WS] Error:', error);
    };

    wsRef.current = ws;
  }, [url, token, enabled, reconnectInterval, maxReconnectAttempts, addNotification, setConnected]);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, [setConnected]);

  const send = useCallback((data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { send, disconnect, isConnected: wsRef.current?.readyState === WebSocket.OPEN };
}
